import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import classNames from "classnames";

import { Body } from "../../../../components/main-body/body";
import Header from "../../../../components/header/header";
import DropdownFilters from "../../../../components/dropdown-filters/dropdown-filters";
import SavedViewsControl from "../../../../components/saved-views-control/saved-views-control";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import useNotification from "../../../../components/notification/notification";

import "../../../../components/main-body/body.css";
import "../../styles.css";
import "./onboardings-report.css";

import ApiAnalytics from "../../../../api/analytics";
import ApiSavedViews from "../../../../api/saved-views";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiSubjectsDashboard from "../../../../api/subjects-dashboard";
import formatAnswerValue from "../../../../common/utils/formatAnswerValue";
import { useCan } from "../../../../common/utils/usePermissions";
import { toCalendarDate, calendarDateToLocalIso } from "../../../../common/utils/responseDateFilter";

const SAVED_VIEWS_SCOPE = "onboardingReports";
const PREVIEW_LIMIT = 25;

// Fixed (non-question) columns. Their headers are short labels that must always
// be shown in full; every other column is an onboarding question whose (possibly
// very long) text is clamped to two lines with a tooltip carrying the full text.
const STATIC_COLUMN_KEYS = new Set([
  "subjectId",
  "cohort",
  "completionDate",
  "localCompletionTime",
  "localTimezone",
]);
// Filter-item id prefix for per-question "filter by response" controls.
const RESPONSE_PREFIX = "response:";

// Collect the response-filter conditions from the filter state. A filter keyed
// `response:<nodeId>` is either a value multi-select (array of strings) or, for
// a message:date question, a date period (array of { start, end }) -> { from, to }
// as CALENDAR days (timezone-safe; see responseDateFilter).
const extractResponseFilters = (filters = {}) =>
  Object.entries(filters)
    .filter(
      ([key, value]) =>
        key.startsWith(RESPONSE_PREFIX) && Array.isArray(value) && value.length
    )
    .map(([key, value]) => {
      const nodeId = key.slice(RESPONSE_PREFIX.length);
      const last = value[value.length - 1];
      if (last && typeof last === "object" && ("start" in last || "end" in last)) {
        const from = toCalendarDate(last.start);
        const to = toCalendarDate(last.end);
        if (!from && !to) return null;
        return { nodeId, from, to };
      }
      return { nodeId, values: value };
    })
    .filter(Boolean);

const defaultFilters = {
  period: [],
  subjectIds: [],
  cohortIds: [],
};

const defaultSorting = {
  sortField: null,
  sortOrder: null,
};

const buildReportOptionLabel = (report) => (
  <div className="onboardings-report__option">
    <span className="onboardings-report__option-title">
      {report.taskTitle || "Untitled onboarding"}
    </span>

    <span className="onboardings-report__option-badge">v{report.version}</span>
  </div>
);

const OnboardingsReport = () => {
  const canExport = useCan("analytics.export");
  const { openNotification } = useNotification();

  const [filters, setFilters] = useState(defaultFilters);
  const [sorting, setSorting] = useState(defaultSorting);
  const [search] = useState("");

  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  const [subjectsList, setSubjectsList] = useState([]);
  const [cohortsList, setCohortsList] = useState([]);

  const [selectedReportOption, setSelectedReportOption] = useState(null);

  const [savedViews, setSavedViews] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);

  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewHasMore, setPreviewHasMore] = useState(false);
  // Onboarding questions (+ options) returned by the preview — drive the
  // "filter by response" controls.
  const [previewQuestions, setPreviewQuestions] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const hydratedRef = useRef(false);
  // Captured at mount: the URL-write effect doesn't keep onboardingSurveyId, so
  // we remember the inbound report reference and resolve it once reports load.
  const initialReportRef = useRef(null);

  const selectedReportId = selectedReportOption?.value || null;

  const notifyError = useCallback(
    (text) => {
      openNotification?.({
        type: "error",
        message: <p>{text}</p>,
      });
    },
    [openNotification]
  );

  const loadSavedViews = useCallback(async () => {
    try {
      setSavedViewsLoading(true);

      const response = await ApiSavedViews.getSavedViews({
        scope: SAVED_VIEWS_SCOPE,
      });

      setSavedViews(response?.data?.savedViews || []);
    } catch (error) {
      console.error("Load saved views error:", error);
    } finally {
      setSavedViewsLoading(false);
    }
  }, []);

  const loadReports = useCallback(async (query = "") => {
    try {
      setReportsLoading(true);

      const response = await ApiAnalytics.getOnboardingReports({
        query: query || undefined,
      });

      setReports(response?.data?.onboardingReports || []);
    } catch (error) {
      console.error("Load onboarding reports error:", error);

      openNotification?.({
        type: "error",
        message: <p>Failed to load onboardings</p>,
      });
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const loadSubjects = useCallback(async () => {
    try {
      const response = await ApiSubjectsManagement.getSubjectsSimple();

      setSubjectsList(
        (response?.data?.subjects || []).map((item) => ({
          id: item.id,
          name: item.displayId || item.id,
        }))
      );
    } catch (error) {
      console.error("Load subjects error:", error);
    }
  }, []);

  const loadCohorts = useCallback(async () => {
    try {
      const response = await ApiSubjectsDashboard.filterField();

      setCohortsList(
        (response?.data?.cohorts || []).map((item) => ({
          id: item.id,
          name: item.value,
        }))
      );
    } catch (error) {
      console.error("Load cohorts error:", error);
    }
  }, []);

  useEffect(() => {
    loadSavedViews();
    loadSubjects();
    loadCohorts();
  }, [loadSavedViews, loadSubjects, loadCohorts]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadReports(search);
    }, 300);

    return () => clearTimeout(timeout);
  }, [search, loadReports]);

  const updateFilterValue = (value) => {
    setFilters(value || defaultFilters);
  };

  const handleApplySavedView = (
    { filters: savedFilters, sorting: savedSorting },
    viewId
  ) => {
    setFilters(savedFilters || defaultFilters);
    setSorting(savedSorting || defaultSorting);
    setSelectedViewId(viewId || null);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setSorting(defaultSorting);
    setSelectedViewId(null);
  };

  const toStartOfDayIso = (value) => {
    if (!value) return undefined;

    const date = new Date(value);
    date.setHours(0, 0, 0, 0);

    return date.toISOString();
  };

  const toEndOfDayIso = (value) => {
    if (!value) return undefined;

    const date = new Date(value);
    date.setHours(23, 59, 59, 999);

    return date.toISOString();
  };

  const buildExportQuery = useCallback(() => {
    if (!selectedReportId) {
      return null;
    }

    const period = Array.isArray(filters.period) && filters.period.length
      ? filters.period[filters.period.length - 1]
      : null;

    const responseFilters = extractResponseFilters(filters);

    return {
      onboardingReportId: selectedReportId,
      startDate: toStartOfDayIso(period?.start),
      endDate: toEndOfDayIso(period?.end),
      subjectIds: filters.subjectIds?.length ? filters.subjectIds : undefined,
      cohortIds: filters.cohortIds?.length ? filters.cohortIds : undefined,
      responseFilters: responseFilters.length ? JSON.stringify(responseFilters) : undefined,
    };
  }, [selectedReportId, filters]);

  const buildPreviewQuery = useCallback(
    (page = 1) => {
      const exportQuery = buildExportQuery();

      if (!exportQuery) {
        return null;
      }

      return {
        ...exportQuery,
        page,
        limit: PREVIEW_LIMIT,
      };
    },
    [buildExportQuery]
  );

  const resetPreview = useCallback(() => {
    setPreviewColumns([]);
    setPreviewRows([]);
    setPreviewPage(1);
    setPreviewHasMore(false);
  }, []);

  const loadPreview = useCallback(
    async (currentPage = 1, replace = false) => {
      try {
        const query = buildPreviewQuery(currentPage);

        if (!query?.onboardingReportId) {
          resetPreview();
          return;
        }

        setPreviewLoading(true);

        const response = await ApiAnalytics.getOnboardingReportPreview(query);

        const nextColumns = response?.data?.columns || [];
        const nextRows = response?.data?.rows || [];
        const pagination = response?.data?.pagination || {};

        setPreviewColumns(nextColumns);
        if (Array.isArray(response?.data?.questions)) {
          setPreviewQuestions(response.data.questions);
        }

        setPreviewRows((prevRows) =>
          replace ? nextRows : [...prevRows, ...nextRows]
        );

        setPreviewPage(currentPage + 1);
        setPreviewHasMore(!!pagination.hasMore);
      } catch (error) {
        console.error("Load onboarding report preview error:", error);

        openNotification?.({
          type: "error",
          message: <p>Failed to load report preview</p>,
        });
      } finally {
        setPreviewLoading(false);
      }
    },
    [buildPreviewQuery, resetPreview]
  );

  useEffect(() => {
    resetPreview();

    if (selectedReportId) {
      loadPreview(1, true);
    }
  }, [selectedReportId, filters, loadPreview, resetPreview]);

  // ---- Unique, shareable URL: hydrate filters from the URL once, then keep
  // the URL in sync with the selected report + applied filters. ----
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    initialReportRef.current = {
      reportId: searchParams.get("onboardingReportId") || searchParams.get("reportId"),
      onboardingSurveyId: searchParams.get("onboardingSurveyId"),
    };

    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");
    const subjectIds = searchParams.get("subjectIds");
    const cohortIds = searchParams.get("cohortIds");
    const responseFiltersRaw = searchParams.get("responseFilters");

    const next = { ...defaultFilters };
    if (startDate || endDate) {
      next.period = [{ start: startDate || undefined, end: endDate || undefined }];
    }
    if (subjectIds) next.subjectIds = subjectIds.split(",").filter(Boolean);
    if (cohortIds) next.cohortIds = cohortIds.split(",").filter(Boolean);
    if (responseFiltersRaw) {
      try {
        const parsed = JSON.parse(responseFiltersRaw);
        if (Array.isArray(parsed)) {
          parsed.forEach((f) => {
            if (!f?.nodeId) return;
            if (f.from || f.to) {
              next[`${RESPONSE_PREFIX}${f.nodeId}`] = [{
                start: calendarDateToLocalIso(f.from),
                end: calendarDateToLocalIso(f.to),
              }];
            } else if (Array.isArray(f.values) && f.values.length) {
              next[`${RESPONSE_PREFIX}${f.nodeId}`] = f.values.map(String);
            }
          });
        }
      } catch (error) {
        // ignore malformed responseFilters in the URL
      }
    }
    setFilters(next);
  }, [searchParams]);

  // Resolve the report from the URL (?onboardingReportId= or ?onboardingSurveyId=)
  // once the report list is available. For an onboardingSurveyId deep link (from
  // analytics) open the report of the survey's ACTIVE version (backend flags it
  // via isActive), falling back to the most-recently-published one.
  useEffect(() => {
    if (selectedReportOption || !reports.length) return;

    const { reportId, onboardingSurveyId } = initialReportRef.current || {};
    if (!reportId && !onboardingSurveyId) return;

    const report = reportId
      ? reports.find((r) => r.id === reportId)
      : reports.find((r) => r.onboardingSurveyId === onboardingSurveyId && r.isActive)
        || reports.find((r) => r.onboardingSurveyId === onboardingSurveyId);

    if (report) {
      setSelectedReportOption({
        value: report.id,
        label: buildReportOptionLabel(report),
        plainLabel: report.taskTitle || "Untitled onboarding",
        version: report.version,
        raw: report,
      });
    }
  }, [reports, selectedReportOption]);

  // Write the current report + filters back to the URL.
  useEffect(() => {
    if (!hydratedRef.current) return;

    const exportQuery = buildExportQuery();
    const next = new URLSearchParams();

    if (selectedReportId) next.set("onboardingReportId", selectedReportId);
    if (exportQuery?.startDate) next.set("startDate", exportQuery.startDate);
    if (exportQuery?.endDate) next.set("endDate", exportQuery.endDate);
    if (exportQuery?.subjectIds) next.set("subjectIds", exportQuery.subjectIds.join(","));
    if (exportQuery?.cohortIds) next.set("cohortIds", exportQuery.cohortIds.join(","));
    if (exportQuery?.responseFilters) next.set("responseFilters", exportQuery.responseFilters);

    setSearchParams(next, { replace: true });
  }, [selectedReportId, filters, buildExportQuery, setSearchParams]);

  const handleExportReport = async () => {
    try {
      const query = buildExportQuery();

      if (!query?.onboardingReportId) {
        notifyError("Select onboarding report first");
        return;
      }

      const response = await ApiAnalytics.exportOnboardingsReport(query);

      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];

      let filename = "onboarding-report.csv";

      if (contentDisposition) {
        const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        const regularMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

        if (utf8Match?.[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else if (regularMatch?.[1]) {
          filename = regularMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export report error:", error);
      notifyError("Failed to export report");
    }
  };

  const reportOptions = useMemo(
    () =>
      reports.map((report) => ({
        value: report.id,
        label: buildReportOptionLabel(report),
        plainLabel: report.taskTitle || "Untitled onboarding",
        version: report.version,
        raw: report,
      })),
    [reports]
  );

  const mergedReportOptions = useMemo(() => {
    if (!selectedReportOption?.value) {
      return reportOptions;
    }

    const exists = reportOptions.some(
      (item) => item.value === selectedReportOption.value
    );

    if (exists) {
      return reportOptions;
    }

    return [selectedReportOption, ...reportOptions];
  }, [reportOptions, selectedReportOption]);

  // Per-question "filter by response" items — one multi-select per question.
  // Questions with predefined options (single/multi/picker/slider) use those;
  // option-less questions (date/time/free text) use the distinct answer values
  // observed in the report (backend `answerOptions`), so they're filterable too.
  // Selecting several values within a question = OR; combining questions = AND.
  const responseFilterItems = useMemo(
    () =>
      (previewQuestions || [])
        .map((q) => {
          // Date questions get a date-range (period) control.
          if (q.nodeType === "message:date") {
            return {
              name: q.questionText || q.nodeId,
              id: `${RESPONSE_PREFIX}${q.nodeId}`,
              period: true,
            };
          }

          const schemaOptions = Array.isArray(q.options) ? q.options : [];
          const labels = schemaOptions.length
            ? schemaOptions.map((opt) => String(opt.label))
            : (Array.isArray(q.answerOptions) ? q.answerOptions.map(String) : []);

          const seen = new Set();
          const children = labels
            .filter((label) => label && !seen.has(label) && seen.add(label))
            .map((label) => ({ id: label, name: label }));

          // LF360-1796: offer "Skipped" as a response-filter value when the
          // question has at least one skipped (optional) answer.
          if (q.hasSkipped && !seen.has("Skipped")) {
            children.push({ id: "Skipped", name: "Skipped" });
          }

          return {
            name: q.questionText || q.nodeId,
            id: `${RESPONSE_PREFIX}${q.nodeId}`,
            children,
          };
        })
        .filter((item) => item.period || (item.children && item.children.length)),
    [previewQuestions]
  );

  const generalFilterItems = useMemo(
    () => [
      {
        name: "Date period",
        id: "period",
        period: true,
      },
      {
        name: "Subject ID",
        id: "subjectIds",
        children: subjectsList,
      },
      {
        name: "Cohort",
        id: "cohortIds",
        children: cohortsList,
      },
    ],
    [subjectsList, cohortsList]
  );

  // Flat list (saved views + active-filter chips).
  const filterItems = useMemo(
    () => [...generalFilterItems, ...responseFilterItems],
    [generalFilterItems, responseFilterItems]
  );

  // Grouped list for the "Add filters" menu — visually separates the response
  // filters from the general ones (only grouped when there are any responses).
  const groupedFilterItems = useMemo(() => {
    if (!responseFilterItems.length) {
      return generalFilterItems;
    }
    return [
      { groupName: "Filters", items: generalFilterItems },
      { groupName: "By response", items: responseFilterItems },
    ];
  }, [generalFilterItems, responseFilterItems]);

  const tableColumns = useMemo(
    () => {
      const renderValue = (value) => {
        const formatted = formatAnswerValue(value);
        return formatted === "N/A" ? "" : formatted;
      };

      return previewColumns.map((column) => {
        const isStatic = STATIC_COLUMN_KEYS.has(column.key);
        // Both header kinds wrap to at most two lines. Static headers are short
        // labels that always show in full (no truncation). Question headers can
        // be long, so they are clamped to two lines with an ellipsis and carry
        // the full text in a tooltip on hover. The table scrolls horizontally,
        // so column widths don't need to fit everything on screen.
        const titleText = column.title;
        return {
          title: isStatic ? (
            <span className="onboardings-report__th">{titleText}</span>
          ) : (
            <Tooltip title={titleText} placement="top">
              <span className="onboardings-report__th onboardings-report__th--clamp" title="">
                {titleText}
              </span>
            </Tooltip>
          ),
          dataIndex: column.key,
          key: column.key,
          width: isStatic ? 180 : 260,
          render: (_, row) => (
            <span className="onboardings-report__cell">
              {renderValue(row[column.key])}
            </span>
          ),
        };
      });
    },
    [previewColumns]
  );

  const stackedFilters = (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        flexWrap: "wrap",
        width: "100%",
        minWidth: 0,
      }}
    >
      <SavedViewsControl
        projectId={window?.currentUser?.projectId}
        scope={SAVED_VIEWS_SCOPE}
        filters={filters}
        sorting={sorting}
        defaultFilters={defaultFilters}
        defaultSorting={defaultSorting}
        filterItems={filterItems}
        sortingOptions={[]}
        savedViews={savedViews}
        savedViewsLoading={savedViewsLoading}
        selectedViewId={selectedViewId}
        onSelectedViewChange={setSelectedViewId}
        onApplySavedView={handleApplySavedView}
        onResetFilters={handleResetFilters}
        onReloadSavedViews={loadSavedViews}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <DropdownFilters
          filtersBy={groupedFilterItems}
          updateFilterValue={updateFilterValue}
          filter={filters}
          mode="controlled"
          savedViewsMode
        />
      </div>
    </div>
  );

  return (
    <Body disabledPaddings={true}>
      <Header
        title="Onboardings"
        stackedFilters={stackedFilters}
        apply={canExport ? {
          label: "Export report",
          onClick: handleExportReport,
          type: "primary",
          disabled: !selectedReportId,
        } : undefined}
      />

      <div className={classNames("lf-content", "onboardings-report")}>
        {reportsLoading ? (
          <div className="onboardings-report__state">
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          </div>
        ) : reports.length === 0 && !selectedReportOption ? (
          <div className="onboardings-report__empty" style={{ padding: 24 }}>
            No onboardings are available for reporting.
          </div>
        ) : (
          <div className="onboardings-report__content">
            <Select
              value={selectedReportOption}
              onChange={(option) => setSelectedReportOption(option || null)}
              placeholder="Select onboarding report"
              className="onboardings-report__select"
              classNames={{ popup: { root: "onboardings-report__popup" } }}
              style={{ width: "100%" }}
              labelInValue
              showSearch
              allowClear
              optionFilterProp="plainLabel"
              filterOption={(input, option) =>
                String(option?.plainLabel || "")
                  .toLowerCase()
                  .includes(String(input || "").toLowerCase())
              }
              options={mergedReportOptions}
            />

            <div className="onboardings-report__preview" style={{ marginTop: 24 }}>
              {selectedReportId && previewLoading && previewRows.length === 0 ? (
                <div className="onboardings-report__state"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 300,
                    width: '100%',
                  }}
                >
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
                </div>
              ) : selectedReportId ? (
                <div className="onboardings-report__table-wrap">
                  <InfinityTable
                    data={previewRows}
                    fetchData={() => loadPreview(previewPage, false)}
                    hasMore={previewHasMore}
                    tableColumns={tableColumns}
                    isLoading={previewLoading}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Body>
  );
};

export default OnboardingsReport;
